import { useEffect, useState } from 'react';
import Modal from '../../components/Common/Modal';
import Button from '../../components/Common/Button';

const RULE_TYPES = [
  { value: 0, key: 'manual' },
  { value: 1, key: 'brand' },
  { value: 2, key: 'gender' },
  { value: 3, key: 'productType' },
  { value: 4, key: 'classification' },
  { value: 5, key: 'fragranceNote' },
  { value: 6, key: 'hasDiscount' },
  { value: 7, key: 'freeShipping' },
];

const RULE_LABELS = {
  manual: { ar: 'يدوي فقط (بدون تكملة تلقائية)', en: 'Manual only (no auto-fill)' },
  brand: { ar: 'براند معيّن', en: 'Specific brand' },
  gender: { ar: 'حسب الفئة (رجالي / نسائي)', en: 'By gender (men / women)' },
  productType: { ar: 'نوع المنتج', en: 'Product type' },
  classification: { ar: 'التصنيف (تجاري / متخصص)', en: 'Classification (designer / niche)' },
  fragranceNote: { ar: 'نوتة العطر', en: 'Fragrance note' },
  hasDiscount: { ar: 'عليه خصم', en: 'Has discount' },
  freeShipping: { ar: 'توصيل مجاني', en: 'Free shipping' },
};

const keyForType = (value) => RULE_TYPES.find((t) => t.value === value)?.key || 'manual';

const emptyForm = {
  titleAr: '',
  titleEn: '',
  sectionType: 0,
  filterValue: '',
  autoFillEnabled: true,
  displayLimit: 8,
  showOnWeb: true,
  showOnMobile: true,
  isActive: true,
};

const SectionFormModal = ({ isOpen, onClose, onSave, section, brands, productTypes, fragranceNotes, isRtl }) => {
  const [form, setForm] = useState(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const isEdit = Boolean(section);
  const isSystem = Boolean(section?.isSystem);

  useEffect(() => {
    if (section) {
      setForm({
        titleAr: section.titleAr || '',
        titleEn: section.titleEn || '',
        sectionType: section.sectionType,
        filterValue: section.filterValue || '',
        autoFillEnabled: section.autoFillEnabled,
        displayLimit: section.displayLimit || 8,
        showOnWeb: section.showOnWeb,
        showOnMobile: section.showOnMobile,
        isActive: section.isActive,
      });
    } else {
      setForm(emptyForm);
    }
    setError('');
  }, [section, isOpen]);

  if (!isOpen) return null;

  const ruleKey = keyForType(form.sectionType);

  const handleSave = async () => {
    if (!form.titleAr.trim() || !form.titleEn.trim()) {
      setError(isRtl ? 'العنوان بالعربي والإنجليزي مطلوبان' : 'Arabic and English titles are required');
      return;
    }
    if (ruleKey !== 'manual' && ['brand', 'productType', 'fragranceNote', 'gender', 'classification'].includes(ruleKey) && !form.filterValue) {
      setError(isRtl ? 'اختر القيمة المطلوبة لهذه القاعدة' : 'Pick a value for this rule');
      return;
    }

    try {
      setIsSaving(true);
      setError('');
      await onSave({
        titleAr: form.titleAr.trim(),
        titleEn: form.titleEn.trim(),
        sectionType: form.sectionType,
        filterValue: ruleKey === 'manual' || ruleKey === 'hasDiscount' || ruleKey === 'freeShipping' ? null : form.filterValue,
        autoFillEnabled: ruleKey === 'manual' ? false : form.autoFillEnabled,
        displayLimit: Number(form.displayLimit) || 8,
        showOnWeb: form.showOnWeb,
        showOnMobile: form.showOnMobile,
        isActive: form.isActive,
      });
    } catch (err) {
      console.error('Error saving section', err);
      setError(isRtl ? 'فشل حفظ القسم' : 'Failed to save section');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? (isRtl ? 'تعديل القسم' : 'Edit section') : (isRtl ? 'قسم جديد' : 'New section')}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            {isRtl ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? (isRtl ? 'جاري الحفظ...' : 'Saving...') : (isRtl ? 'حفظ' : 'Save')}
          </Button>
        </>
      )}
    >
      {error && <div className="text-danger" style={{ marginBottom: 'var(--sp-3)' }}>{error}</div>}

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">{isRtl ? 'العنوان بالعربي *' : 'Arabic title *'}</label>
          <input
            className="form-input"
            value={form.titleAr}
            onChange={(e) => setForm({ ...form, titleAr: e.target.value })}
            dir="rtl"
          />
        </div>
        <div className="form-group">
          <label className="form-label">{isRtl ? 'العنوان بالإنجليزي *' : 'English title *'}</label>
          <input
            className="form-input"
            value={form.titleEn}
            onChange={(e) => setForm({ ...form, titleEn: e.target.value })}
          />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">{isRtl ? 'نوع القسم' : 'Section type'}</label>
        <select
          className="form-select"
          value={form.sectionType}
          disabled={isSystem}
          onChange={(e) => setForm({ ...form, sectionType: Number(e.target.value), filterValue: '' })}
        >
          {RULE_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {isRtl ? RULE_LABELS[t.key].ar : RULE_LABELS[t.key].en}
            </option>
          ))}
        </select>
        {isSystem && (
          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', marginTop: 'var(--sp-1)' }}>
            {isRtl ? 'قسم أساسي — لا يمكن تغيير نوعه.' : "Built-in section — its type can't be changed."}
          </div>
        )}
      </div>

      {ruleKey === 'brand' && (
        <div className="form-group">
          <label className="form-label">{isRtl ? 'البراند' : 'Brand'}</label>
          <select
            className="form-select"
            value={form.filterValue}
            onChange={(e) => setForm({ ...form, filterValue: e.target.value })}
          >
            <option value="">{isRtl ? 'اختر البراند' : 'Select a brand'}</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>{isRtl ? (b.nameAr || b.nameEn) : b.nameEn}</option>
            ))}
          </select>
        </div>
      )}

      {ruleKey === 'productType' && (
        <div className="form-group">
          <label className="form-label">{isRtl ? 'نوع المنتج' : 'Product type'}</label>
          <select
            className="form-select"
            value={form.filterValue}
            onChange={(e) => setForm({ ...form, filterValue: e.target.value })}
          >
            <option value="">{isRtl ? 'اختر نوع المنتج' : 'Select a product type'}</option>
            {productTypes.map((pt) => (
              <option key={pt.id} value={pt.id}>{isRtl ? (pt.nameAr || pt.nameEn) : pt.nameEn}</option>
            ))}
          </select>
        </div>
      )}

      {ruleKey === 'fragranceNote' && (
        <div className="form-group">
          <label className="form-label">{isRtl ? 'نوتة العطر' : 'Fragrance note'}</label>
          <select
            className="form-select"
            value={form.filterValue}
            onChange={(e) => setForm({ ...form, filterValue: e.target.value })}
          >
            <option value="">{isRtl ? 'اختر النوتة' : 'Select a note'}</option>
            {fragranceNotes.map((n) => (
              <option key={n.id} value={n.id}>{isRtl ? (n.nameAr || n.nameEn) : n.nameEn}</option>
            ))}
          </select>
        </div>
      )}

      {ruleKey === 'gender' && (
        <div className="form-group">
          <label className="form-label">{isRtl ? 'الفئة' : 'Gender'}</label>
          <select
            className="form-select"
            value={form.filterValue}
            onChange={(e) => setForm({ ...form, filterValue: e.target.value })}
          >
            <option value="">{isRtl ? 'اختر' : 'Select'}</option>
            <option value="male">{isRtl ? 'رجالي' : 'Men'}</option>
            <option value="female">{isRtl ? 'نسائي' : 'Women'}</option>
          </select>
        </div>
      )}

      {ruleKey === 'classification' && (
        <div className="form-group">
          <label className="form-label">{isRtl ? 'التصنيف' : 'Classification'}</label>
          <select
            className="form-select"
            value={form.filterValue}
            onChange={(e) => setForm({ ...form, filterValue: e.target.value })}
          >
            <option value="">{isRtl ? 'اختر' : 'Select'}</option>
            <option value="designer">{isRtl ? 'تجاري' : 'Designer'}</option>
            <option value="niche">{isRtl ? 'متخصص' : 'Niche'}</option>
          </select>
        </div>
      )}

      {ruleKey !== 'manual' && (
        <div className="form-group">
          <label className="form-label form-label-inline">
            <input
              type="checkbox"
              checked={form.autoFillEnabled}
              onChange={(e) => setForm({ ...form, autoFillEnabled: e.target.checked })}
            />
            <span>
              {isRtl
                ? 'إكمال تلقائي (إذا اخترت منتجات أقل من الحد، تُكمَّل الباقي تلقائياً حسب القاعدة)'
                : 'Auto-fill (if you pick fewer products than the limit, the rest fill in automatically)'}
            </span>
          </label>
        </div>
      )}

      <div className="form-group">
        <label className="form-label">{isRtl ? 'عدد المنتجات المعروضة بالصفحة الرئيسية' : 'Products shown on the home page'}</label>
        <input
          type="number"
          min="1"
          max="40"
          className="form-input"
          value={form.displayLimit}
          onChange={(e) => setForm({ ...form, displayLimit: e.target.value })}
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label form-label-inline">
            <input
              type="checkbox"
              checked={form.showOnWeb}
              onChange={(e) => setForm({ ...form, showOnWeb: e.target.checked })}
            />
            <span>{isRtl ? 'يظهر بالموقع' : 'Show on website'}</span>
          </label>
        </div>
        <div className="form-group">
          <label className="form-label form-label-inline">
            <input
              type="checkbox"
              checked={form.showOnMobile}
              onChange={(e) => setForm({ ...form, showOnMobile: e.target.checked })}
            />
            <span>{isRtl ? 'يظهر بالتطبيق' : 'Show on mobile app'}</span>
          </label>
          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', marginTop: 'var(--sp-1)' }}>
            {isRtl
              ? 'يعمل مع النسخة القادمة من التطبيق فقط.'
              : 'Takes effect with the upcoming app release only.'}
          </div>
        </div>
      </div>

      {isEdit && (
        <div className="form-group">
          <label className="form-label form-label-inline">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            />
            <span>{isRtl ? 'مفعّل' : 'Active'}</span>
          </label>
        </div>
      )}
    </Modal>
  );
};

export default SectionFormModal;
